import { reactive, toValue, type MaybeRefOrGetter } from 'vue';

/**
 * Persisted open/closed state for the search inspectors' collapsible sections
 * ("Playback Settings", "Pending Commands", "Logs", …), keyed per entity so a
 * collapsed player section doesn't collapse the same-named section on another
 * entity. A module-level reactive cache keeps every surface (split pane,
 * side-peek overlay) in sync live; localStorage makes it survive reloads.
 */
const STORAGE_KEY = 'search-inspector-sections';

type SectionState = Record<string, Record<string, boolean>>;

function loadStoredState(): SectionState {
    if (typeof localStorage === 'undefined') {
        return {};
    }
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? (parsed as SectionState) : {};
    } catch {
        return {};
    }
}

const state = reactive<SectionState>(loadStoredState());

function persist() {
    if (typeof localStorage === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Storage may be unavailable — best-effort only.
    }
}

export function useInspectorSections(entityId: MaybeRefOrGetter<string>) {
    function isOpen(sectionTitle: string, defaultOpen = true): boolean {
        return state[toValue(entityId)]?.[sectionTitle] ?? defaultOpen;
    }

    function setOpen(sectionTitle: string, open: boolean): void {
        const entity = toValue(entityId);
        if (!state[entity]) {
            state[entity] = {};
        }
        state[entity][sectionTitle] = open;
        persist();
    }

    return { isOpen, setOpen };
}

/** Test hook: wipe the in-memory cache (does not touch localStorage). */
export function resetInspectorSectionsForTesting(): void {
    for (const key of Object.keys(state)) {
        delete state[key];
    }
}
